import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

export type ButtonSize = 's' | 'm' | 'l' | 'xl' | 'xxl';
export type ButtonVariant = 'primary' | 'destructive' | 'secondaryGray' | 'secondaryColor' | 'link';

export const buttonSizeSpecs: Record<ButtonSize, SystemStyleObject<Theme>> = {
  s: {
    height: 36,
    minWidth: 120,
    fontSize: '14px',
    padding: '8px 12px',
    borderRadius: '8px',
    gap: '4px',
  },
  m: {
    height: 40,
    minWidth: 154,
    fontSize: '14px',
    padding: '10px 14px',
    borderRadius: '8px',
    gap: '4px',
  },
  l: {
    height: 44,
    minWidth: 164,
    fontSize: '16px',
    padding: '10px 16px',
    borderRadius: '8px',
    gap: '6px',
  },
  xl: {
    height: 48,
    minWidth: 178,
    fontSize: '16px',
    padding: '12px 18px',
    borderRadius: '8px',
    gap: '6px',
  },
  xxl: {
    height: 56,
    minWidth: 200,
    fontSize: '18px',
    padding: '16px 22px',
    borderRadius: '10px',
    gap: '8px',
  },
};

export const buttonVariantStyles:
Record<ButtonVariant, (theme: Theme) => SystemStyleObject<Theme>> = {

  primary: (theme) => ({
    border: 'none',
    backgroundColor: theme.palette.brand.primary,
    color: theme.palette.common.white,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    transition: 'background-image 0.15s ease',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.brand.primary,
      backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.1))',
    },
    '&:active': {
      backgroundColor: theme.palette.brand.primary,
      backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[400],
      border: `1px solid ${theme.palette.grey[200]}`,
    },
    '&:focus-visible': {
      outline: 'none',
    },
  }),

  // A primary button for actions that destroy something, such as confirming a
  // delete: the same states as primary, in the error color.
  destructive: (theme) => ({
    border: 'none',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    transition: 'background-image 0.15s ease',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.error.main,
      backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.1))',
    },
    '&:active': {
      backgroundColor: theme.palette.error.main,
      backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[400],
      border: `1px solid ${theme.palette.grey[200]}`,
    },
    '&:focus-visible': {
      outline: 'none',
    },
  }),

  secondaryGray: (theme) => ({
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[800],
    border: `1px solid ${theme.palette.grey[800]}`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    transition: 'background-image 0.15s ease',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.common.white,
      backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
    },
    '&:active': {
      backgroundColor: theme.palette.common.white,
      backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[400],
    },
  }),

  secondaryColor: (theme) => ({
    border: 'none',
    backgroundColor: theme.palette.brand.secondary,
    color: theme.palette.brand.primary,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    transition: 'background-image 0.15s ease',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.brand.secondary,
      backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
    },
    '&:active': {
      backgroundColor: theme.palette.brand.secondary,
      backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[400],
    },
  }),

  link: (theme) => ({
    // Renders as an inline text link: no button chrome (padding/min-width/
    // border/background), body2 typography, brand-primary color, no underline.
    padding: '0px',
    height: 'auto',
    minWidth: 'auto',
    border: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.brand.primary,
    fontSize: '16px',
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.brand.primary,
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      textDecoration: 'none',
    },
    '&:active': {
      backgroundImage: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
      color: theme.palette.grey[400],
      textDecoration: 'none',
      border: 'none',
    },
  }),
};
